//RecordDetailContainer.tsx
import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { RecordDetail } from "./RecordDetail";
import { ApplicationData, Referent, ResponseActivity } from "./ApplicationData";
import { axiosRequest } from "../../service/AxiosService";
import {
  ReportAndFiles,
  saveNewData,
  saveReportsAndFiles,
} from "../../service/RecordDetailService";
import { approveVote } from "../../service/ApproveVoteService";
import { rejectVote } from "../../service/RejectVoteService";
import { sendBack } from "../../service/SendBackService";
import { useUser } from "../../context/UserContext";
import { canceled } from "../../service/CanceledService";

/**
 * 申請詳細コンテナのプロパティ型
 */
interface ApplicationDataProps {
  applicationEntryId: string; // 申請エントリID
  previousData: ApplicationData; // 前画面から引き継いだ申請データ
  previousApplicationFile: Record<number, File[]>; // 前画面から引き継いだ添付ファイル
  imageFiles: File[]; // 前画面から引き継いだ画像ファイル
}

/**
 * 申請詳細コンテナコンポーネント
 * - 申請データの取得・保存・各種申請操作（承認・却下・差し戻し・取り下げ）を管理する
 * - props: applicationEntryId, previousData, previousApplicationFile, imageFiles
 */
export const RecordDetailContainer = ({
  applicationEntryId,
  previousData,
  previousApplicationFile,
  imageFiles,
}: ApplicationDataProps) => {
  const [applicationData, setApplicationData] = useState(previousData);
  const [applicationFile, setApplicationFile] = useState<
    Record<number, File[]>
  >(previousApplicationFile);

  const [loading, setLoading] = useState<boolean>(true);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const idempotencyKey = useRef<string | null>(null);
  const isSubmitting = useRef<boolean>(false);
  const shouldBlockBack = useRef<boolean>(false);
  const historyPushed = useRef<boolean>(false);
  const fromCustomForm = useRef<boolean>(false);

  // 前画面がCustomFormかどうかを記録
  useEffect(() => {
    const from = location.state?.from ?? "view";
    if (from === "CustomForm") {
      fromCustomForm.current = true;
    }
  }, [location.state]);

  // ブラウザで前画面に戻る際のダイアログ表示
  useEffect(() => {
    if (!fromCustomForm.current) return; // 前画面は申請内容入力画面の場合だけ処理を行う

    // ブロックフラグを有効にする（但初回ロードは除く）
    shouldBlockBack.current = true;

    // ブラウザで戻る際の処理
    const handlePopState = () => {
      if (!shouldBlockBack.current) return;

      // ブラウザバックをキャンセルするため、現在の状態をプッシュ
      window.history.pushState(null, "", window.location.href);

      // 確認ダイアログを表示
      setShowBackDialog(true);
    };

    // 初回マウント時のみ、現在の状態をプッシュしてブラウザバックをキャッチできるようにする
    if (!historyPushed.current) {
      window.history.pushState(null, "", window.location.href);
      historyPushed.current = true;
    }

    // EventListenerを追加
    window.addEventListener("popstate", handlePopState);

    // EventListenerをクリア
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // ブラウザバック確認ダイアログで「戻る」を選択した場合
  const handleConfirmBack = () => {
    setShowBackDialog(false);
    // フラグをクリアしてから戻る
    shouldBlockBack.current = false;
    // CustomForm画面に直接遷移する（データは渡さない）
    navigate(`/CustomForm/${applicationData.applicationFormatId}`, {
      replace: true, // 現在のhistoryエントリを置き換える
    });
  };

  // ブラウザバック確認ダイアログで「留まる」を選択した場合
  const handleCancelBack = () => {
    setShowBackDialog(false);
  };

  //入力内容確認画面から入力画面に戻る際
  const handleGoBackToEdit = () => {
    // switch型の値を文字列から布尔值に変換してから渡す
    const formattedData = {
      ...applicationData,
      formEntryList: applicationData.formEntryList?.map((formData) => {
        if (formData.type === "switch") {
          return {
            ...formData,
            value: formData.value === "true" || formData.value === true,
          };
        }
        return formData;
      }),
    };

    navigate(`/CustomForm/${applicationData.applicationFormatId}`, {
      state: {
        applicationData: formattedData,
        applicationFiles: applicationFile,
      },
    });
  };

  //申請記録内容のコピー処理
  const handleUseThisFormat = () => {
    // 申請書フォーマットが削除されている場合は処理しない
    if (applicationData.isFormatDeleted) {
      console.error("この申請書フォーマットは削除されています");
      return;
    }

    const {
      applicationFormatId,
      formEntryList,
      approvalActivityList,
      referent,
    } = applicationData;

    //formEntryListにあるアップロードファイルをクリアする
    const selectedFormEntryList = formEntryList.map((entry) => ({
      ...entry,
      value: entry.type === "fileUpload" ? undefined : entry.value,
    }));

    //ログイン中のユーザーを申請者として設定する
    const newApplicant = {
      id: user?.id || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      profileList: [],
    };

    // 必要な承認者情報だけ抽出
    const selectedApprovalActivityList = (approvalActivityList ?? []).map(
      (activity) => ({
        applicationActivityId: activity.applicationActivityId,
        activityId: activity.activityId,
        activityName: activity.activityName,
        order: activity.order,
        approverList: (activity.approverList ?? []).map((approver) => ({
          id: approver.id,
          approverFirstName: approver.approverFirstName,
          approverLastName: approver.approverLastName,
        })),
      })
    );

    // 必要な参照者情報だけ抽出
    const selectedReferent: Referent = {
      userList: (referent?.userList ?? []).map((user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
      })),
      profileList: referent?.profileList,
      groupList: referent?.groupList,
    };

    // コピーデータに完全なApplicationData構造を補足する（要らないデータはNULLと設定する）
    const copiedApplicationData: ApplicationData = {
      applicationFormatId,
      type: "",
      name: "",
      description: "",
      isExpress: false,
      formEntryList: selectedFormEntryList,
      applicant: newApplicant,
      approvalActivityList: selectedApprovalActivityList,
      referent: selectedReferent,
      commentList: [],
      applicantList: [],
      activityList: [],
      responseActivityList: [],
      number: "",
      submittedAt: undefined,
      status: "",
    };

    //コピーデータを持って申請内容入力画面に遷移する
    console.log("copiedApplicationData", copiedApplicationData);
    navigate(`/CustomForm/${applicationFormatId}`, {
      state: { copiedData: copiedApplicationData },
    });
  };


  // BASE_URL を編集画面へ渡す前に解決
  const resolveRichTextUrls = (html?: string) => {
    if (!html) return html;
    const BASE_URL = process.env.REACT_APP_BASE_URL ?? "";
    // 文字列置換で十分なら簡易に
    return html.replace(/\$\{BASE_URL\}/g, BASE_URL);
  };



  // 既存: fileUpload の index をキーにしていた
  // ↓ 現在: formId をキーにする（複数ファイル＆後方互換対応）
  const handleReapplication = async () => {
    const reApplicationData: ApplicationData = { ...applicationData };
    const reApplicationEntryId = applicationData.applicationEntryId;

    // fileUpload を全件抽出（value が undefined / null でないもの）
    const fileUploadEntries = (applicationData.formEntryList ?? [])
      .filter(f => f.type === "fileUpload" && f.value != null);

    // formIdベースのマップ
    const reApplicationFilesByFormId: Record<string, File[]> = {};

    if (fileUploadEntries.length > 0) {
      for (const entry of fileUploadEntries) {
        const names = parseAttachedNames(entry.value); // ← 追加
        if (names.length === 0) continue;

        const files: File[] = [];
        for (const name of names) {
          try {
            // 現状APIは単一 Blob を返す想定のためファイルごとに1回ダウンロード
            const blob = await axiosRequest<Blob>("/download/attachedFile", "POST", {
              params: { applicationEntryId, fileName: name },
              responseType: "blob",
            });
            const file = new File([blob], name, {
              type: blob.type,
              lastModified: Date.now(),
            });
            files.push(file);
          } catch (error) {
            console.error("ファイルダウンロードエラー:", error);
          }
        }

        // formId キーで保持（空ならスキップ）
        const key = entry.formId?.trim();
        if (key) {
          reApplicationFilesByFormId[key] = files;
        }
      }
    }

    // richText の ${BASE_URL} を編集遷移前に解決（既存実装）
    reApplicationData.formEntryList =
      reApplicationData.formEntryList?.map((entry) => {
        if (entry.type === "richText" && typeof entry.value === "string") {
          return { ...entry, value: resolveRichTextUrls(entry.value) };
        }
        return entry;
      }) ?? [];

    // formIdベースのマップを state に載せて遷移（既存の遷移を維持）
    navigate(`/CustomForm/${reApplicationData.applicationFormatId}`, {
      state: {
        copiedData: reApplicationData,
        reApplicationFilesByFormId,
        reApplicationEntryId,
      },
    });
  };



  /**
   * 添付ファイルのvalue値からファイル名の配列を抽出する
   * JSON形式、カンマ区切り形式、配列形式に対応し、後方互換性を保つ
   * @param value - ファイル名を含む値（文字列、配列、またはその他の型）
   * @returns ファイル名の配列（空文字列は除外）
   */
  const parseAttachedNames = (value: unknown): string[] => {
    // 1) 文字列なら JSON優先 → 後方互換（カンマ区切り）
    if (typeof value === "string") {
      const s = value.trim();
      // JSONならパース（配列のみ採用）
      if (s.startsWith("[") || s.startsWith("{")) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) {
            return parsed
              .map(v => {
                if (typeof v === "string") return v;
                // 配列要素がオブジェクトの場合の保険（{name} / {fileName} 想定）
                if (v && typeof v === "object") {
                  const anyV: any = v;
                  if (typeof anyV.name === "string") return anyV.name;
                  if (typeof anyV.fileName === "string") return anyV.fileName;
                }
                return String(v);
              })
              .map(v => v.trim())
              .filter(v => v.length > 0);
          }
          // 配列でない JSON は無視して後方互換へ
        } catch {
          // JSONパース失敗 → 後方互換へ
        }
      }
      // 2) 後方互換：旧データ（カンマ区切り）
      return s
        .split(",")
        .map(x => x.trim())
        .filter(x => x.length > 0);
    }

    // 3) value が配列型で来た場合の保険
    if (Array.isArray(value)) {
      return (value as any[])
        .map(v => (typeof v === "string" ? v : String(v)))
        .map(v => v.trim())
        .filter(v => v.length > 0);
    }

    return [];
  };

  const { user } = useUser(); // useUser フックを使ってユーザー情報を取得

  // 共通の操作実行・結果判断・画面遷移
  const handleAction = async (
    actionName: string, // 操作名を画面遷移際のactionとして伝達
    operation: () => Promise<boolean>, // 特定処理するこための操作メソッド
    activityCode?: string // 対象のactivityCode（任意）
  ) => {
    try {
      const result = await operation(); // 操作メソッドを実行
      if (result) {
        let activityName: string | undefined = undefined;
        if (activityCode) {
          activityName = applicationData.activityList?.find(
            (a) => a.code === activityCode && a.status === "InProgress"
          )?.activityName;
        }

        navigate("/OperationCompleted", {
          //完了画面に表示する文字はactivityNameあるいはactionName（activityNameはactivityCodeによって違う、存在しない場合は渡したactionNameを表示する）：
          state: {
            action: actionName,
            activityName: activityName ?? actionName, // なければactionNameを使う
          },
        });

        // navigate後に非同期で少し待ってからリセット
        setTimeout(() => {
          isSubmitting.current = false;
          idempotencyKey.current = null;
        }, 1000);
      } else {
        console.log(`${actionName} 操作に失敗しました。`);
      }
    } catch (error) {
      console.error(`${actionName} 操作異常：`, error);
    }
  };

  // 申請承認処理
  const handleApproveVote = (comment: string) => {
    if (!user || !user.id) {
      console.error("ユーザー情報が取得できません");
      return;
    }
    handleAction(
      "承認",
      () => approveVote(user.id, applicationData, comment),
      "1"
    );
  };

  // 申請却下処理
  const handleRejectVote = (comment: string) => {
    if (!user || !user.id) {
      console.error("ユーザー情報が取得できません");
      return;
    }
    handleAction("却下", () => rejectVote(user.id, applicationData, comment));
  };

  // 差し戻し処理
  const handleSendBack = (comment: string) => {
    if (!user || !user.id) {
      console.error("ユーザー情報が取得できません");
      return;
    }
    handleAction("差し戻し", () => sendBack(user.id, applicationData, comment));
  };

  // 取り下げ処理
  const handleCanceled = () => {
    if (!user || !user.id) {
      console.error("ユーザー情報が取得できません");
      return;
    }
    handleAction("取り下げ", () => canceled(user.id, applicationData, ""));
  };

  //データ保存処理
  const handleSaveNewData = () => {
    handleAction("保存", () => saveNewData(applicationData, null));
  };

  //申請処理
  const handleApplicationDecision = () => {
    // 二重送信を防止するため、Idempotency-keyを生成する
    if (!isSubmitting.current) {
      // 初回送信時にのみIdempotency-keyを生成
      idempotencyKey.current = idempotencyKey.current ?? crypto.randomUUID();
      isSubmitting.current = true;
    }
    // statusは設定せず、バックエンド側で処理する。再申請か否かはsaveNewDataにて判定しているため、ここでは不要。アクション名も分けずに申請に統一する。
    handleAction("申請", () => saveNewData(applicationData, applicationFile, imageFiles, idempotencyKey));
  };

  //添付ファイルダウンロード処理
  const handleDownloadFile = async (fileName: string) => {
    const applicationEntryId = applicationData.applicationEntryId;
    if (!applicationEntryId || !fileName) {
      return;
    }

    //ダウンロード処理
    try {
      const blob = await axiosRequest<Blob>("/download/attachedFile", "POST", {
        params: { applicationEntryId, fileName },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("ファイルダウンロードエラー:", error);
    } finally {
    }
  };
  const handleSaveReportData = async (report: string, files: File[], responseActivity: ResponseActivity) => {

    const userId = user?.id;
    if (!responseActivity?.activityId) {
      console.error("activityIdが取得できません");
      return;
    }
    const saveData: ReportAndFiles = {
      userId: userId,
      activityId: responseActivity.activityId,
      applicationNumber: applicationData.number,
      report: report,
      fileList: files,
    };

    handleAction("応答", () => saveReportsAndFiles(saveData), "4");
  };
  useEffect(() => {
    if (!previousData) {
      //選択された申請書IDを使用する。
      fetchApplicationData(applicationEntryId);
    } else {
      setApplicationData(previousData);
      setLoading(false);
    }
    if (previousApplicationFile) {
      //選択された申請書IDを使用する。
      setApplicationFile(previousApplicationFile);
    }
  }, [
    applicationEntryId,
    applicationFile,
    previousApplicationFile,
    previousData,
  ]);

  const fetchApplicationData = async (id: string) => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await axiosRequest<ApplicationData>(
        "/applicationAggregate/getById", "GET", { params: { id } }
      );
      setApplicationData(data);
    } catch (error: any) {
      //念のためnullを代入
      setApplicationData(null as any);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  }

  if (!applicationData) {
    return <Typography>データが見つかりませんでした。</Typography>;
  }

  return (
    <Box>
      <RecordDetail
        applicationData={applicationData}
        handleSaveNewData={handleSaveNewData}
        handleApplicationDecision={handleApplicationDecision}
        handleApproveVote={handleApproveVote}
        handleRejectVote={handleRejectVote}
        handleSendBack={handleSendBack}
        handleDownloadFile={handleDownloadFile}
        handleSaveReportData={handleSaveReportData}
        onClickGoBackToEdit={handleGoBackToEdit}
        handleUseThisFormat={handleUseThisFormat}
        handleReapplication={handleReapplication}
        handleCanceled={handleCanceled}
      />

      {/* ブラウザバック確認ダイアログ */}
      <Dialog open={showBackDialog} onClose={handleCancelBack}>
        <DialogTitle>入力内容が失われる可能性があります</DialogTitle>
        <DialogContent>
          <DialogContentText>
            このページを離れると、入力した情報は保存されません。内容を修正する場合は、「内容修正」ボタンを押してください。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBack}>キャンセル</Button>
          <Button onClick={handleConfirmBack} color="error">
            ページを離れる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
